import { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, orderBy, query, where } from 'firebase/firestore';
import { db } from '../firebase';

function sameYmd(d) {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
}

function overlaps(aStart, aEnd, bStart, bEnd) {
  return aStart < bEnd && bStart < aEnd;
}

export function useUpcomingAssignedEvents(user, plannings, { daysAhead = 30 } = {}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!user || !plannings?.length) {
        setItems([]);
        return;
      }
      setLoading(true);
      try {
        const now = new Date();
        const until = new Date(now.getTime() + daysAhead * 24 * 60 * 60 * 1000);
        const results = [];
        await Promise.all(
          plannings.map(async (p) => {
            const col = collection(db, 'plannings', p.id, 'events');
            const pushDoc = (d) => {
              const data = d.data();
              const s = data.start?.toDate ? data.start.toDate() : new Date(data.start);
              const e = data.end?.toDate ? data.end.toDate() : new Date(data.end || data.start);
              // Filter by window
              if (s < now || s > until) return;
              results.push({
                id: d.id,
                planningId: p.id,
                planningName: p.name || 'Calendar',
                title: data.title || 'Event',
                start: s,
                end: e,
              });
            };
            try {
              // Preferred: array-contains + date window + orderBy (requires composite index)
              const q1 = query(
                col,
                where('assignedTo', 'array-contains', user.uid),
                where('start', '>=', now),
                where('start', '<=', until),
                orderBy('start', 'asc')
              );
              const snap = await getDocs(q1);
              snap.docs.forEach(pushDoc);
            } catch {
              try {
                // Fallback: array-contains only, filter dates client-side
                const q2 = query(col, where('assignedTo', 'array-contains', user.uid));
                const snap2 = await getDocs(q2);
                snap2.docs.forEach(pushDoc);
              } catch {
                // ignore this planning on error
              }
            }
          })
        );
        if (!cancelled) setItems(results.sort((a, b) => a.start - b.start));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [user, plannings, daysAhead]);

  // Group by date and compute conflicts across calendars
  const { groups, conflictIds } = useMemo(() => {
    const byDate = new Map();
    for (const ev of items) {
      const key = sameYmd(ev.start);
      if (!byDate.has(key)) byDate.set(key, []);
      byDate.get(key).push(ev);
    }
    const conflict = new Set();
    for (const [, list] of byDate) {
      const sorted = list.slice().sort((a, b) => a.start - b.start);
      for (let i = 0; i < sorted.length; i++) {
        for (let j = i + 1; j < sorted.length; j++) {
          const a = sorted[i];
          const b = sorted[j];
          if (b.start >= a.end) break;
          if (a.planningId !== b.planningId && overlaps(a.start, a.end, b.start, b.end)) {
            conflict.add(a.id);
            conflict.add(b.id);
          }
        }
      }
    }
    // Convert to ordered array of groups
    const groupsArr = Array.from(byDate.entries()).sort(([a], [b]) => (a < b ? -1 : 1)).map(([dateKey, list]) => ({ dateKey, events: list }));
    return { groups: groupsArr, conflictIds: conflict };
  }, [items]);

  return { loading, groups, conflictIds };
}
